import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, UserFocus, ClipboardText } from "@phosphor-icons/react";
import { useLang } from "@/lib/LanguageContext";
import { wa } from "@/lib/pfd";
import { card, SERVICE_IMG } from "@/lib/images";
import { COUNTRY_LINKS } from "@/lib/pfdLandingCountries";
import PageShell from "@/components/PageShell";
import Seo from "@/components/Seo";
import ImageHero from "@/components/ImageHero";
import ImageCard from "@/components/ImageCard";

const trustIcons = [ShieldCheck, UserFocus, ClipboardText];

export default function Home() {
  const { t, lang } = useLang();
  const h = t.home;

  return (
    <PageShell>
      <Seo
        title={
          lang === "fr"
            ? "Assistant personnel & conciergerie privée | Book & Smile"
            : "Personal Assistant & Private Concierge Paris | Book & Smile"
        }
        description={
          lang === "fr"
            ? "Conciergerie privée et assistant personnel à Paris pour dirigeants internationaux, familles et voyageurs exigeants. Un interlocuteur bilingue de confiance."
            : "Private concierge and personal assistant in Paris for international executives, families and discerning travelers. One trusted bilingual contact."
        }
      />

      <ImageHero
        testId="home-hero"
        titleTestId="hero-headline"
        image="heroHome"
        alt="Paris street with the Eiffel Tower at golden hour"
        eyebrow={h.eyebrow}
        titleA={h.h1a}
        titleB={h.h1b}
        text={h.answer}
        tall
      >
        <Link
          data-testid="hero-cta-primary"
          to="/contact"
          className="inline-flex items-center justify-center gap-3 bg-stone-50 text-slate-900 px-8 py-4 text-sm font-medium hover:bg-stone-200 transition-colors"
        >
          {h.ctaPrimary}
          <ArrowRight size={16} />
        </Link>
        <a
          data-testid="hero-cta-whatsapp"
          href={wa(h.waMsgPrimary)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center gap-3 border border-stone-50/40 text-stone-50 px-8 py-4 text-sm font-medium hover:border-stone-50 transition-colors"
        >
          {lang === "fr" ? "Parler à votre assistant privé" : "Speak With Your Private Assistant"}
        </a>
        <p data-testid="hero-meta" className="sm:hidden text-xs tracking-[0.18em] uppercase text-stone-300">{h.heroMeta}</p>
      </ImageHero>

      {/* Commercial message */}
      <section data-testid="home-commercial" className="py-16 sm:py-32 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <h2 className="md:col-span-6 font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-stone-900 leading-[1.1]">{h.commercialTitle}</h2>
          <div className="md:col-span-5 md:col-start-8">
            <p className="text-stone-600 leading-relaxed text-base md:text-lg">{h.commercialP1}</p>
            <p data-testid="commercial-signature" className="mt-6 font-serif text-2xl italic text-stone-900">
              {h.commercialSignature}
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" data-testid="home-services" className="bg-stone-100 border-y border-stone-200 py-16 sm:py-32 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="eyebrow">{h.servicesEyebrow}</p>
              <h2 className="font-serif mt-6 text-base md:text-lg text-stone-900">{h.servicesTitle}</h2>
            </div>
            <Link data-testid="home-services-all" to="/services" className="inline-flex items-center gap-2 text-sm text-stone-800 border-b border-stone-300 hover:border-stone-900 py-2 transition-colors">
              {t.nav.services} <ArrowRight size={13} />
            </Link>
          </div>
          <nav data-testid="home-core-services" aria-label="Core services" className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {[["/private-concierge-paris", "Private Concierge Paris", "Conciergerie privée Paris"], ["/personal-assistant-paris", "Personal Assistant Paris", "Assistant personnel Paris"], ["/executive-assistant-paris", "Executive Assistant Paris", "Executive assistant Paris"], ["/corporate-concierge-paris", "Corporate Concierge Paris", "Conciergerie d'entreprise Paris"], ["/luxury-concierge-paris", "Luxury Concierge Paris", "Conciergerie de luxe Paris"]].map(([to, en, fr]) => (
              <Link key={to} to={to} className="py-2 text-stone-700 hover:text-stone-900 border-b border-transparent hover:border-stone-900 transition-colors">{lang === "fr" ? fr : en}</Link>
            ))}
          </nav>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {t.services.items.map((s) => (
              <ImageCard
                key={s.id}
                to={s.route}
                testId={`home-service-${s.id}`}
                image={SERVICE_IMG[s.id]}
                eyebrow={s.eyebrow}
                title={s.title}
                text={s.text}
                cta={s.cta}
                aspect="aspect-[4/3] sm:aspect-[4/5]"
              />
            ))}
          </div>
          <div className="mt-16 bg-slate-900 p-10 sm:p-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <p className="eyebrow !text-stone-400">{h.advantageEyebrow}</p>
              <h3 className="font-serif mt-4 text-2xl sm:text-3xl text-stone-50 leading-snug">{h.advantageTitle}</h3>
            </div>
            <p className="lg:col-span-4 text-stone-400 leading-relaxed text-sm">{h.advantageText}</p>
          </div>
        </div>
      </section>

      {/* Fashion Week */}
      <section data-testid="home-fashion-week" className="py-16 px-6 lg:px-10 border-b border-stone-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <p className="eyebrow">Paris Fashion Week</p>
            <h2 className="font-serif mt-4 text-2xl sm:text-3xl text-stone-900">{lang === "fr" ? "Une conciergerie Fashion Week, par des gens qui vivent ici." : "A Fashion Week concierge, run by people who live here."}</h2>
          </div>
          <Link data-testid="home-fashion-week-link" to="/paris-fashion-week-concierge" className="inline-flex items-center gap-3 border border-stone-900 text-stone-900 px-8 py-4 text-sm font-medium hover:bg-slate-900 hover:text-stone-50 transition-colors">
            {lang === "fr" ? "Voir le programme" : "See the programme"} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Who we serve */}
      <section data-testid="home-who-we-serve" className="py-16 sm:py-32 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 overflow-hidden aspect-[4/3] lg:aspect-[4/5] bg-stone-200">
            <img src={card("windowView")} alt={lang === "fr" ? "Dîner avec vue sur la tour Eiffel, Paris" : "Dinner with a view of the Eiffel Tower, Paris"} loading="lazy" className="w-full h-full object-cover" />
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="eyebrow">{h.whoEyebrow}</p>
            <h2 className="font-serif mt-6 text-base md:text-lg text-stone-900">{h.whoTitle}</h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-8">
              {h.whoItems.map((item, i) => (
                <div key={item.title} data-testid={`who-item-${i}`} className="border-t border-stone-200 pt-5">
                  <h3 className="font-medium text-stone-900">{item.title}</h3>
                  <p className="mt-3 text-stone-600 leading-relaxed text-sm">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Worldwide */}
      <section data-testid="home-worldwide" className="bg-stone-100 border-y border-stone-200 py-16 sm:py-28 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="eyebrow">{lang === "fr" ? "Clients du monde entier" : "Clients worldwide"}</p>
            <h2 className="font-serif mt-6 text-base md:text-lg text-stone-900">
              {lang === "fr" ? "Ouvert tous les jours. Paris géré, quel que soit votre fuseau horaire." : "Open every day. Paris handled, whatever your time zone."}
            </h2>
            <p className="mt-6 text-stone-600 leading-relaxed text-sm">
              {lang === "fr"
                ? "Nos clients nous briefent depuis leur pays, des semaines avant d'arriver. 8h – 20h heure de Paris pour les nouvelles demandes ; 24h/24 pour les membres."
                : "Our clients brief us from home, weeks before they land. 8:00 – 20:00 Paris time for new requests; 24/7 for members."}
            </p>
          </div>
          <div className="lg:col-span-7 flex flex-wrap gap-3 content-start">
            {COUNTRY_LINKS.map((c) => (
              <Link
                key={c.slug}
                to={`/${c.slug}`}
                data-testid={`country-link-${c.slug}`}
                className="border border-stone-300 hover:border-stone-900 hover:bg-stone-50 px-5 py-3 text-sm text-stone-800 transition-colors"
              >
                {c[lang]}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Membership teaser */}
      <section data-testid="home-membership-teaser" className="relative overflow-hidden bg-slate-900">
        <img src={card("cafeLaptop")} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/70 to-slate-950/30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 sm:py-36 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <p className="eyebrow !text-stone-400">{h.membershipTeaserEyebrow}</p>
            <h2 className="font-serif mt-6 text-3xl sm:text-4xl lg:text-5xl font-light text-stone-50 leading-[1.1]">{h.membershipTeaserTitle}</h2>
            <p className="mt-6 text-stone-300 leading-relaxed max-w-xl">{h.membershipTeaserText}</p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              data-testid="home-membership-cta"
              to="/membership"
              className="inline-flex items-center gap-3 bg-stone-50 text-slate-900 px-8 py-4 text-sm font-medium hover:bg-stone-200 transition-colors"
            >
              {h.membershipTeaserCta}
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section data-testid="home-trust" className="py-16 sm:py-32 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <p className="eyebrow">{h.trustEyebrow}</p>
          <h2 className="font-serif mt-6 text-base md:text-lg text-stone-900">{h.trustTitle}</h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-stone-200 border border-stone-200">
            {h.trustItems.map((item, i) => {
              const Icon = trustIcons[i];
              return (
                <div key={item.title} className="bg-stone-50 p-10 sm:p-12">
                  <Icon size={26} className="text-stone-900" />
                  <h3 className="mt-6 font-medium text-stone-900">{item.title}</h3>
                  <p className="mt-3 text-stone-600 leading-relaxed text-sm">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Founder */}
      <section data-testid="home-founder" className="py-16 sm:py-32 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="eyebrow">{h.founderEyebrow}</p>
            <h2 className="font-serif mt-6 text-3xl sm:text-4xl text-stone-900 leading-tight">{h.founderTitle}</h2>
            <p className="mt-6 text-stone-600 leading-relaxed text-base md:text-lg">{h.founderText}</p>
            <Link data-testid="home-founder-link" to="/about" className="mt-6 inline-flex items-center gap-2 text-sm text-stone-800 border-b border-stone-300 hover:border-stone-900 transition-colors py-2">
              {h.founderLink} <ArrowRight size={13} />
            </Link>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 overflow-hidden aspect-[4/3] lg:aspect-[4/5] bg-stone-200">
            <img src={card("apartment")} alt={lang === "fr" ? "Salon parisien classique" : "Classic Parisian living room"} loading="lazy" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="home-faq" className="bg-stone-100 border-t border-stone-200 py-16 sm:py-28 px-6 lg:px-10">
        <div className="max-w-4xl mx-auto">
          <p className="eyebrow">FAQ</p>
          <h2 className="font-serif mt-6 text-base md:text-lg text-stone-900">{h.faqTitle}</h2>
          <div className="mt-10 space-y-8">
            {h.faqs.map((f, i) => (
              <div key={f.q} data-testid={`home-faq-${i}`} className="border-b border-stone-200 pb-6">
                <h3 className="font-medium text-stone-900">{f.q}</h3>
                <p className="mt-2 text-stone-600 leading-relaxed text-sm">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="home-final-cta" className="relative overflow-hidden bg-slate-900 text-center">
        <img src={card("heroHomeAlt")} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-slate-950/60" />
        <div className="relative max-w-3xl mx-auto px-6 py-20 sm:py-36">
          <h2 className="font-serif text-4xl sm:text-5xl font-light text-stone-50 italic">{t.finalCta.title}</h2>
          <p className="mt-6 text-stone-300 text-lg">{t.finalCta.text}</p>
          <Link
            data-testid="final-cta-contact"
            to="/contact"
            className="mt-10 inline-flex items-center justify-center gap-3 bg-stone-50 text-slate-900 px-8 py-4 text-sm font-medium hover:bg-stone-200 transition-colors"
          >
            {t.finalCta.cta}
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
